import { GpsPoint } from '../processed/GpsPoint'
import {
  TelemetryIndex,
  createTelemetryIndex,
  IndexCreationOptions,
} from './TelemetryIndex'

export interface IndexedTelemetry<T = GpsPoint> {
  points: T[]
  index: TelemetryIndex
}

export function createIndexedTelemetry<
  T extends { distanceMeters: number; elapsedMs: number; timestamp: Date },
>(points: T[], options?: IndexCreationOptions): IndexedTelemetry<T> {
  return {
    points,
    index: createTelemetryIndex(points, options),
  }
}

export function findPointAtDistance<T>(
  indexed: IndexedTelemetry<T>,
  distanceMeters: number,
  resolutionMeters: number = 5
): T | null {
  const roundedDistance =
    Math.round(distanceMeters / resolutionMeters) * resolutionMeters
  const index = indexed.index.byDistance.get(roundedDistance)
  return index !== undefined ? indexed.points[index] : null
}

export function findPointAtElapsedTime<T>(
  indexed: IndexedTelemetry<T>,
  elapsedMs: number,
  resolutionMs: number = 100
): T | null {
  const roundedTime = Math.round(elapsedMs / resolutionMs) * resolutionMs
  const index = indexed.index.byTime.get(roundedTime)
  return index !== undefined ? indexed.points[index] : null
}

export function findPointAtTimestamp<T>(
  indexed: IndexedTelemetry<T>,
  timestamp: Date
): T | null {
  const roundedTimestamp = Math.round(timestamp.getTime() / 1000) * 1000
  const index = indexed.index.byTimestamp.get(roundedTimestamp)
  return index !== undefined ? indexed.points[index] : null
}

export function getPointsInDistanceRange<T extends { distanceMeters: number }>(
  indexed: IndexedTelemetry<T>,
  startMeters: number,
  endMeters: number
): T[] {
  return indexed.points.filter(
    (point) =>
      point.distanceMeters >= startMeters && point.distanceMeters <= endMeters
  )
}

export function getPointsInTimeRange<T extends { elapsedMs: number }>(
  indexed: IndexedTelemetry<T>,
  startMs: number,
  endMs: number
): T[] {
  return indexed.points.filter(
    (point) => point.elapsedMs >= startMs && point.elapsedMs <= endMs
  )
}
